import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Button,
  Descriptions,
  Tag,
  message,
  Space,
  Avatar,
  Spin,
  Typography,
  Empty,
  Alert,
  ConfigProvider,
} from 'antd';
import {
  ArrowRightOutlined,
  PhoneOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  RobotOutlined,
  SoundOutlined,
  UserOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  TeamOutlined,
  DollarOutlined,
  UserAddOutlined,
  BankOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  AudioOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { callCenterCallsApi } from '../../utils/api';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { getImagePath } from '../../utils/imageHelper';
import { DetailPageSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { useCallCenterOptional } from '../../contexts/CallCenterContext';
import { toJalali } from '../../utils/dateUtils';

const { Text } = Typography;


const DIRECTION_META = {
  incoming: { label: 'ورودی', color: 'blue', icon: <ArrowDownOutlined /> },
  outgoing: { label: 'خروجی', color: 'green', icon: <ArrowUpOutlined /> },
};

const DISPOSITION_META = {
  ANSWERED: { label: 'پاسخ داده', color: 'green' },
  'NO ANSWER': { label: 'بی‌پاسخ', color: 'orange' },
  NOANSWER: { label: 'بی‌پاسخ', color: 'orange' },
  BUSY: { label: 'مشغول', color: 'gold' },
  FAILED: { label: 'ناموفق', color: 'red' },
};

const AI_STATUS_META = {
  none: { label: 'بدون پردازش', color: 'default' },
  pending: { label: 'در صف پردازش', color: 'processing' },
  processing: { label: 'در حال پردازش', color: 'processing' },
  completed: { label: 'انجام شده', color: 'success' },
  failed: { label: 'خطا', color: 'error' },
};

const TYPE_META = {
  client: { label: 'مشتری', color: 'green', icon: <TeamOutlined />, path: (id) => `/clients/${id}` },
  lead: { label: 'سرنخ', color: 'orange', icon: <UserAddOutlined />, path: (id) => `/leads/${id}` },
  deal: { label: 'معامله', color: 'blue', icon: <DollarOutlined />, path: (id) => `/deals/${id}` },
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  const s = Number(seconds) || 0;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const normalizeAiStatus = (status) => String(status || 'none').toLowerCase();
const isAiInProgress = (status) => {
  const s = normalizeAiStatus(status);
  return s === 'pending' || s === 'processing';
};

/** کارت ریز برای نمایش سایر رکوردهای منطبق با کلیک برای جابجایی */
const MatchChip = ({ item, type, onNavigate }) => {
  const meta = TYPE_META[type] || TYPE_META.client;
  const name = item.name || item.client_name || '—';
  const sub =
    item.mobile ||
    item.phone ||
    item.contact?.mobile ||
    item.contact?.client_name ||
    item.client?.name ||
    item.email ||
    item.cell ||
    '';

  return (
    <div
      onClick={() => {
        if (item.id) onNavigate(meta.path(item.id));
      }}
      className="cursor-pointer flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-teal-400 dark:hover:border-teal-500 hover:shadow-sm transition-all group"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: type === 'client' ? 'rgba(16,185,129,0.12)' : type === 'lead' ? 'rgba(249,115,22,0.12)' : 'rgba(59,130,246,0.12)',
            color: type === 'client' ? '#059669' : type === 'lead' ? '#ea580c' : '#2563eb',
          }}
        >
          {meta.icon}
        </div>
        <div className="min-w-0">
          <div className="custom-font font-medium text-gray-800 dark:text-gray-100 text-sm truncate">{name}</div>
          {sub && (
            <div className="custom-font text-xs text-gray-500 truncate" dir="ltr">
              {sub}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Tag color={meta.color} className="custom-font m-0 text-xs">
          {meta.label}
        </Tag>
        <ArrowLeftOutlined className="text-gray-300 group-hover:text-teal-500 transition-colors text-xs" />
      </div>
    </div>
  );
};

/** متن بلند داخل کارت — بدون بیرون‌زدگی (عرض محدود، ارتفاع متناسب با محتوا) */
const ContainedTextBlock = ({ children }) => (
  <div
    className="call-center-contained-text"
    style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
    }}
  >
    <div
      className="call-center-contained-text__inner custom-font"
      style={{
        fontFamily: 'CustomFont, sans-serif',
        fontSize: 14,
        lineHeight: 1.75,
        margin: 0,
        padding: 16,
        borderRadius: 8,
        border: '1px solid rgba(0,0,0,0.06)',
        backgroundColor: 'var(--ant-color-bg-container, #fff)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        maxWidth: '100%',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {children}
    </div>
  </div>
);

const ViewCallCenterCall = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const callCenter = useCallCenterOptional();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();

  const goBackToList = () => {
    navigate(returnToRef.current || '/call-center/calls', { state: buildGoBackState() });
  };

  const [loading, setLoading] = useState(true);
  const [call, setCall] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pollingAi, setPollingAi] = useState(false);
  const pollIntervalRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const aiAlertedRef = useRef(false);
  const transcribeAlertedRef = useRef(false);

  const stopAiPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setPollingAi(false);
  }, []);

  const loadAudio = useCallback(async (callId) => {
    setAudioLoading(true);
    try {
      const res = await callCenterCallsApi.getAudioLink(callId);
      const link = res?.data?.audio_link;
      if (link) {
        setAudioUrl(link);
      } else {
        setAudioUrl(null);
        message.warning('لینک فایل صوتی در دسترس نیست');
      }
    } catch (error) {
      console.error('خطا در دریافت فایل صوتی:', error);
      setAudioUrl(null);
      message.error(error?.message || 'خطا در دریافت فایل صوتی');
    } finally {
      setAudioLoading(false);
    }
  }, []);

  const applyCallData = useCallback((data) => {
    setCall(data);
    if (data?.audio_url && !audioUrl) {
      setAudioUrl(data.audio_url);
    }
    return normalizeAiStatus(data?.ai_status);
  }, [audioUrl]);

  const fetchCallStatus = useCallback(async ({ silent = false, showRefresh = false } = {}) => {
    if (showRefresh) setRefreshing(true);
    else if (!silent) setLoading(true);
    try {
      const res = await callCenterCallsApi.getById(id);
      const data = res?.data ?? null;
      const status = applyCallData(data);
      return { data, status };
    } catch (error) {
      if (!silent) {
        console.error('خطا در دریافت جزئیات تماس:', error);
        message.error(error?.message || 'خطا در دریافت جزئیات تماس');
      }
      return { data: null, status: null };
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, applyCallData]);

  const handlePlayAudio = async () => {
    if (audioUrl) return;
    await loadAudio(id);
  };

  const pollAiStatusOnce = useCallback(async () => {
    if (pollInFlightRef.current) return;
    pollInFlightRef.current = true;
    try {
      const { status: aiStatus, data } = await fetchCallStatus({ silent: true });
      const tStatus = data?.transcribe_status;

      if (aiStatus === 'completed' && !aiAlertedRef.current) {
        aiAlertedRef.current = true;
        message.success('پردازش هوش مصنوعی با موفقیت انجام شد');
      } else if (aiStatus === 'failed' && !aiAlertedRef.current) {
        aiAlertedRef.current = true;
        message.error('پردازش هوش مصنوعی با خطا مواجه شد');
      }

      if (tStatus === 'completed' && !transcribeAlertedRef.current) {
        transcribeAlertedRef.current = true;
        message.success('رونویسی مکالمه با موفقیت انجام شد');
      } else if (tStatus === 'failed' && !transcribeAlertedRef.current) {
        transcribeAlertedRef.current = true;
        message.error('رونویسی مکالمه با خطا مواجه شد');
      }

      if (!isAiInProgress(aiStatus) && tStatus !== 'pending' && tStatus !== 'processing') {
        stopAiPolling();
      }
    } finally {
      pollInFlightRef.current = false;
    }
  }, [fetchCallStatus, stopAiPolling]);

  const startAiPolling = useCallback(() => {
    stopAiPolling();
    setPollingAi(true);
    pollIntervalRef.current = setInterval(() => {
      pollAiStatusOnce();
    }, 10000); // Polling every 10s for both AI and Transcribe
  }, [pollAiStatusOnce, stopAiPolling]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { status: aiStatus, data } = await fetchCallStatus();
      if (cancelled) return;
      const tStatus = data?.transcribe_status;
      aiAlertedRef.current = !isAiInProgress(aiStatus);
      transcribeAlertedRef.current = (tStatus !== 'pending' && tStatus !== 'processing');
      if (isAiInProgress(aiStatus) || tStatus === 'pending' || tStatus === 'processing') {
        startAiPolling();
      }
    })();
    return () => {
      cancelled = true;
      stopAiPolling();
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSummarize = async () => {
    setSummarizing(true);
    try {
      const res = await callCenterCallsApi.summarize(id);
      message.success(res?.message || 'درخواست خلاصه‌سازی در صف قرار گرفت');
      aiAlertedRef.current = false;
      const { status: aiStatus, data } = await fetchCallStatus({ silent: true, showRefresh: true });
      const tStatus = data?.transcribe_status;
      if (isAiInProgress(aiStatus) || tStatus === 'pending' || tStatus === 'processing') {
        startAiPolling();
      }
    } catch (error) {
      console.error('خطا در خلاصه‌سازی:', error);
      message.error(error?.message || 'خطا در درخواست خلاصه‌سازی');
    } finally {
      setSummarizing(false);
    }
  };

  const handleTranscribe = async () => {
    setTranscribing(true);
    try {
      const res = await callCenterCallsApi.transcribe(id);
      message.success(res?.message || 'درخواست تبدیل صوت به متن در صف پردازش قرار گرفت.');
      transcribeAlertedRef.current = false;
      const { status: aiStatus, data } = await fetchCallStatus({ silent: true, showRefresh: true });
      const tStatus = data?.transcribe_status;
      if (isAiInProgress(aiStatus) || tStatus === 'pending' || tStatus === 'processing') {
        startAiPolling();
      }
    } catch (error) {
      console.error('خطا در رونویسی:', error);
      message.error(error?.message || 'خطا در درخواست رونویسی');
    } finally {
      setTranscribing(false);
    }
  };

  // اطلاعات تطبیقی برگشتی از API
  const match = useMemo(() => call?.match || {}, [call?.match]);
  const primary = match?.primary || null;
  const primaryMeta = primary?.type ? TYPE_META[primary.type] : null;

  const otherMatches = useMemo(() => {
    const items = [];
    (match.clients || []).forEach((c) => {
      if (!(primary?.type === 'client' && Number(primary?.id) === Number(c.id))) {
        items.push({ item: c, type: 'client' });
      }
    });
    (match.leads || []).forEach((l) => {
      if (!(primary?.type === 'lead' && Number(primary?.id) === Number(l.id))) {
        items.push({ item: l, type: 'lead' });
      }
    });
    (match.deals || []).forEach((d) => {
      if (!(primary?.type === 'deal' && Number(primary?.id) === Number(d.id))) {
        items.push({ item: d, type: 'deal' });
      }
    });
    return items;
  }, [match, primary]);

  if (loading) {
    return (
      <MainLayout>
        <div className="custom-font" style={{ fontFamily: 'CustomFont, sans-serif' }}>
          <DetailPageSkeleton />
        </div>
      </MainLayout>
    );
  }

  if (!call) {
    return (
      <MainLayout>
        <ConfigProvider theme={{ token: { fontFamily: 'CustomFont, sans-serif' } }}>
          <Card className="shadow-xl border-0 custom-font">
            <Empty description={<span className="custom-font">تماس یافت نشد</span>} />
            <div className="text-center mt-4">
              <Button className="custom-font" onClick={goBackToList}>
                بازگشت به لیست
              </Button>
            </div>
          </Card>
        </ConfigProvider>
      </MainLayout>
    );
  }

  const dirMeta = DIRECTION_META[call.direction] || { label: call.direction, color: 'default' };
  const dispMeta = DISPOSITION_META[call.disposition] || { label: call.disposition, color: 'default' };
  const aiStatus = normalizeAiStatus(call.ai_status);
  const aiMeta = AI_STATUS_META[aiStatus] || AI_STATUS_META.none;
  const canSummarize = aiStatus !== 'completed' && !isAiInProgress(aiStatus);
  const isAiPending = isAiInProgress(aiStatus) || pollingAi;
  const isAiCompleted = aiStatus === 'completed';
  const isAiFailed = aiStatus === 'failed';
  
  const tStatus = call.transcribe_status || (call.is_transcribed ? 'completed' : 'none');
  const isTranscribePending = tStatus === 'pending';
  const isTranscribeProcessing = tStatus === 'processing';
  const isTranscribeFailed = tStatus === 'failed';
  const isTranscribeCompleted = tStatus === 'completed';
  const canTranscribe = call.has_audio !== false && (audioUrl || call.audio_url || call.audio_file) && (tStatus === 'none' || tStatus === 'failed');

  const customerPhone = call.phone || (call.direction === 'outgoing' ? call.dst : call.src) || '';

  return (
    <MainLayout>
      <ConfigProvider theme={{ token: { fontFamily: 'CustomFont, sans-serif' } }}>
        <div className="custom-font w-full max-w-full overflow-x-hidden" style={{ fontFamily: 'CustomFont, sans-serif', minWidth: 0 }}>
          <Card className="shadow-xl border-0 custom-font w-full max-w-full" bodyStyle={{ padding: 0, fontFamily: 'CustomFont, sans-serif', overflow: 'hidden', maxWidth: '100%' }}>
            {/* نوار عنوان صفحه */}
            <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
              <div className="flex items-center gap-3">
                <Button
                  type="text"
                  icon={<ArrowRightOutlined />}
                  onClick={goBackToList}
                  className="custom-font"
                />
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center shadow-lg">
                  <PhoneOutlined className="text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0 custom-font">
                    جزئیات تماس
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0 custom-font" dir="ltr">
                    {customerPhone || '—'}
                  </p>
                </div>
              </div>
              <Space wrap>
                {customerPhone && callCenter?.clickToCall && (
                  <Button
                    icon={<PhoneOutlined />}
                    onClick={() => callCenter.clickToCall(customerPhone)}
                    className="custom-font"
                  >
                    تماس مجدد
                  </Button>
                )}
                <Button
                  icon={<ReloadOutlined />}
                  loading={refreshing}
                  onClick={() => fetchCallStatus({ showRefresh: true })}
                  className="custom-font"
                >
                  بروزرسانی
                </Button>
                {canTranscribe && (
                  <Button
                    icon={<AudioOutlined />}
                    loading={transcribing}
                    onClick={handleTranscribe}
                    className="custom-font text-purple-600 border-purple-500 hover:text-purple-700 hover:border-purple-600"
                  >
                    ارسال برای رونویسی
                  </Button>
                )}
                {isTranscribePending && (
                  <Button disabled icon={<ClockCircleOutlined />} className="custom-font">
                    در صف رونویسی...
                  </Button>
                )}
                {isTranscribeProcessing && (
                  <Button disabled icon={<LoadingOutlined />} className="custom-font text-purple-500 border-purple-300">
                    در حال رونویسی...
                  </Button>
                )}
                <PermissionGate permission="manage_callcenter_setting">
                  {canSummarize && (
                    <Button
                      type="primary"
                      icon={<RobotOutlined />}
                      loading={summarizing}
                      onClick={handleSummarize}
                      className={`${PANEL_PRIMARY_BTN_CLASS} custom-font`}
                    >
                      خلاصه‌سازی با AI
                    </Button>
                  )}
                </PermissionGate>
              </Space>
            </div>

            {/* بدنه محتوا */}
            <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl space-y-5 custom-font w-full max-w-full overflow-x-hidden" style={{ minWidth: 0 }}>
              
              {/* بخش ۱: کارت مخاطب تطبیق‌یافته در CRM (دقیقاً مشابه پاپ‌آپ زنگ خوردن) */}
              {primary ? (
                <div className="rounded-2xl border-2 border-teal-300 dark:border-teal-700 bg-gradient-to-br from-white via-teal-50/40 to-emerald-50/30 dark:from-gray-900 dark:via-gray-850 dark:to-teal-950/20 shadow-sm p-5 transition-all">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <Avatar
                        size={64}
                        className="shrink-0 bg-gradient-to-br from-teal-500 to-emerald-600 text-white font-bold text-2xl shadow-md"
                        src={getImagePath(primary.image)}
                        icon={!primary.name && !primary.image ? <UserOutlined /> : undefined}
                      >
                        {(primary.name || '?').charAt(0)}
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="custom-font text-xl font-bold text-gray-900 dark:text-white">
                            {primary.name || '—'}
                          </span>
                          {primaryMeta && (
                            <Tag color={primaryMeta.color} className="custom-font m-0 font-medium">
                              {primaryMeta.label}
                            </Tag>
                          )}
                          <Tag color="cyan" className="custom-font m-0 text-xs">
                            مخاطب اصلی شناسایی‌شده
                          </Tag>
                        </div>
                        {primary.company_name && (
                          <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 custom-font mb-1">
                            <BankOutlined className="text-teal-600 dark:text-teal-400" />
                            <span>{primary.company_name}</span>
                          </div>
                        )}
                        {primary.contact_name && (
                          <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 custom-font mb-1">
                            <UserOutlined className="text-blue-500" />
                            <span>مخاطب معامله: {primary.contact_name}</span>
                          </div>
                        )}
                        {primary.mobile && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 custom-font" dir="ltr">
                            {primary.mobile}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* دکمه‌های اقدام سریع */}
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      {customerPhone && callCenter?.clickToCall && (
                        <Button
                          icon={<PhoneOutlined />}
                          onClick={() => callCenter.clickToCall(customerPhone)}
                          className="custom-font"
                        >
                          تماس سریع
                        </Button>
                      )}
                      {primaryMeta && primary.id && (
                        <Button
                          type="primary"
                          icon={<ArrowLeftOutlined />}
                          onClick={() => navigate(primaryMeta.path(primary.id))}
                          className={PANEL_PRIMARY_BTN_CLASS}
                        >
                          مشاهده پروفایل {primaryMeta.label}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* سایر تطبیق‌های یافت‌شده برای این شماره */}
                  {otherMatches.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-teal-100 dark:border-teal-900/60">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 custom-font">
                        سایر تطبیق‌های CRM با این شماره ({otherMatches.length} مورد):
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {otherMatches.map(({ item, type }) => (
                          <MatchChip
                            key={`${type}-${item.id}`}
                            item={item}
                            type={type}
                            onNavigate={navigate}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-5 text-center">
                  <UserOutlined className="text-3xl text-gray-400 mb-2" />
                  <p className="custom-font font-medium text-gray-700 dark:text-gray-200 m-0">
                    مخاطبی در CRM برای این شماره ثبت نشده است
                  </p>
                  <p className="custom-font text-gray-400 m-0 text-xs mt-1" dir="ltr">
                    {customerPhone}
                  </p>
                  <div className="mt-3 flex items-center justify-center gap-2">
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => navigate(`/leads/add?mobile=${encodeURIComponent(customerPhone)}`)}
                      className="custom-font"
                    >
                      افزودن به عنوان سرنخ
                    </Button>
                    <Button
                      size="small"
                      icon={<UserAddOutlined />}
                      onClick={() => navigate(`/clients/create?mobile=${encodeURIComponent(customerPhone)}`)}
                      className="custom-font"
                    >
                      افزودن به عنوان مشتری
                    </Button>
                  </div>
                </div>
              )}

              {/* بخش ۲: اطلاعات فنی تماس */}
              <Card
                size="small"
                className="custom-font shadow-sm"
                title={<span className="custom-font font-bold">اطلاعات تماس</span>}
              >
                <Descriptions
                  column={{ xs: 1, sm: 2, md: 3 }}
                  size="small"
                  className="custom-font"
                  labelStyle={{ fontFamily: 'CustomFont, sans-serif' }}
                  contentStyle={{ fontFamily: 'CustomFont, sans-serif' }}
                >
                  <Descriptions.Item label="نوع تماس">
                    <Tag color={dirMeta.color} icon={dirMeta.icon} className="custom-font">
                      {dirMeta.label}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="وضعیت">
                    <Tag color={dispMeta.color} className="custom-font">{dispMeta.label}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="شماره مخاطب">
                    <Text className="custom-font" dir="ltr">{call.phone || '—'}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="داخلی پاسخگو">
                    <Text className="custom-font" dir="ltr">{call.extension || '—'}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="صف تماس">
                    <Text className="custom-font" dir="ltr">{call.queue || '—'}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="زمان انتظار">
                    <Text className="custom-font" dir="ltr">{formatDuration(call.wait)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="مدت مکالمه">
                    <Text className="custom-font" dir="ltr">{formatDuration(call.billsec ?? call.duration)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="زمان تماس">
                    <Text className="custom-font" dir="ltr">
                      {call.started_at
                        ? toJalali(call.started_at, 'jYYYY/jMM/jDD HH:mm:ss') || new Date(call.started_at).toLocaleString('fa-IR')
                        : '—'}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="زمان پاسخگویی">
                    <Text className="custom-font" dir="ltr">
                      {call.answered_at
                        ? toJalali(call.answered_at, 'jYYYY/jMM/jDD HH:mm:ss') || new Date(call.answered_at).toLocaleString('fa-IR')
                        : '—'}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="زمان پایان">
                    <Text className="custom-font" dir="ltr">
                      {call.ended_at
                        ? toJalali(call.ended_at, 'jYYYY/jMM/jDD HH:mm:ss') || new Date(call.ended_at).toLocaleString('fa-IR')
                        : '—'}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="وضعیت AI">
                    <Space size={8}>
                      <Tag color={aiMeta.color} className="custom-font">{aiMeta.label}</Tag>
                      {isAiPending && <Spin size="small" />}
                    </Space>
                  </Descriptions.Item>
                  {call.simotel_uniqueid && (
                    <Descriptions.Item label="شناسه یکتا (UniqueID)">
                      <Text className="custom-font text-xs text-gray-500" dir="ltr">
                        {call.simotel_uniqueid}
                      </Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>

              {/* بخش ۳: طرفین تماس (اپراتور و مخاطب) */}
              {(call.client || call.lead || call.deal || call.agent) && (
                <Card size="small" className="custom-font shadow-sm" title={<span className="custom-font font-bold">طرفین مکالمه</span>}>
                  <div className="flex flex-wrap gap-8">
                    {call.agent && (
                      <div className="flex items-center gap-3">
                        <Avatar src={getImagePath(call.agent.image)} size={48} className="bg-teal-600 text-white font-bold">
                          {call.agent.name?.charAt(0)}
                        </Avatar>
                        <div>
                          <div className="custom-font font-medium text-gray-800 dark:text-gray-100">{call.agent.name}</div>
                          <div className="custom-font text-xs text-gray-500">اپراتور پاسخ‌دهنده (داخلی {call.extension || '—'})</div>
                        </div>
                      </div>
                    )}
                    {call.client && (
                      <div className="flex items-center gap-3">
                        <Avatar src={getImagePath(call.client.image)} size={48} icon={<UserOutlined />} className="bg-emerald-600 text-white" />
                        <div>
                          <div className="custom-font font-medium text-gray-800 dark:text-gray-100">{call.client.name}</div>
                          <div className="custom-font text-xs text-gray-500">مشتری متصل به تماس</div>
                          {call.client_user_id && (
                            <Button
                              type="link"
                              size="small"
                              className="custom-font p-0 h-auto"
                              onClick={() => navigate(`/clients/${call.client_user_id}`)}
                            >
                              مشاهده پروفایل مشتری
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                    {!call.client && call.lead && (
                      <div className="flex items-center gap-3">
                        <Avatar size={48} icon={<UserAddOutlined />} className="bg-orange-600 text-white" />
                        <div>
                          <div className="custom-font font-medium text-gray-800 dark:text-gray-100">{call.lead.client_name || call.lead.company_name}</div>
                          <div className="custom-font text-xs text-gray-500">سرنخ متصل به تماس</div>
                          {call.lead_id && (
                            <Button
                              type="link"
                              size="small"
                              className="custom-font p-0 h-auto"
                              onClick={() => navigate(`/leads/${call.lead_id}`)}
                            >
                              مشاهده پروفایل سرنخ
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                    {!call.client && !call.lead && call.deal && (
                      <div className="flex items-center gap-3">
                        <Avatar size={48} icon={<DollarOutlined />} className="bg-blue-600 text-white" />
                        <div>
                          <div className="custom-font font-medium text-gray-800 dark:text-gray-100">{call.deal.name}</div>
                          <div className="custom-font text-xs text-gray-500">معامله متصل به تماس</div>
                          {call.deal_id && (
                            <Button
                              type="link"
                              size="small"
                              className="custom-font p-0 h-auto"
                              onClick={() => navigate(`/deals/${call.deal_id}`)}
                            >
                              مشاهده معامله
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* بخش ۴: پخش صوت مکالمه */}
              <Card
                size="small"
                className="custom-font shadow-sm"
                title={
                  <span className="custom-font font-bold flex items-center gap-2">
                    <SoundOutlined className="text-teal-600" />
                    فایل صوتی مکالمه
                  </span>
                }
              >
                <div className="space-y-3">
                  {call.has_audio === false && !audioUrl ? (
                    <div className="text-gray-400 text-sm custom-font">
                      فایل صوتی برای این تماس ضبط نشده است.
                    </div>
                  ) : !audioUrl ? (
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      loading={audioLoading}
                      onClick={handlePlayAudio}
                      className={`${PANEL_PRIMARY_BTN_CLASS} custom-font`}
                    >
                      دریافت و پخش فایل صوتی
                    </Button>
                  ) : (
                    <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-xl">
                      <audio
                        key={audioUrl}
                        src={audioUrl}
                        controls
                        autoPlay
                        style={{ width: '100%' }}
                        className="custom-font"
                      >
                        مرورگر شما از پخش صوت پشتیبانی نمی‌کند.
                      </audio>
                    </div>
                  )}
                </div>
              </Card>

              {/* بخش ۵: خلاصه‌سازی و ترنسکریپت مکالمه */}
              {(isAiPending || isAiCompleted || isAiFailed) && (
                <Card
                  size="small"
                  className="custom-font shadow-sm call-center-summary-card"
                  title={
                    <span className="custom-font font-bold flex items-center gap-2">
                      <RobotOutlined className="text-indigo-500" />
                      خلاصه مکالمه (هوش مصنوعی)
                    </span>
                  }
                  style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
                >
                  {isAiPending ? (
                    <div className="text-center py-6 custom-font">
                      <Spin tip={<span className="custom-font">در حال پردازش هوش مصنوعی... (بررسی وضعیت هر ۳۰ ثانیه)</span>} />
                    </div>
                  ) : isAiFailed ? (
                    <Alert
                      type="error"
                      showIcon
                      className="custom-font"
                      message={<span className="custom-font">خطا در پردازش هوش مصنوعی</span>}
                      description={
                        <span className="custom-font">
                          {call.ai_error || 'پردازش با خطا مواجه شد. می‌توانید مجدداً خلاصه‌سازی را اجرا کنید.'}
                        </span>
                      }
                    />
                  ) : call.summary ? (
                    <ContainedTextBlock>{call.summary}</ContainedTextBlock>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={<span className="custom-font">خلاصه‌ای موجود نیست</span>}
                    />
                  )}
                </Card>
              )}

              {isAiCompleted && call.transcript && (
                <Card
                  size="small"
                  className="custom-font shadow-sm call-center-transcript-card"
                  title={<span className="custom-font font-bold">رونوشت کامل گفتگو (Transcript)</span>}
                  style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
                >
                  <ContainedTextBlock>{call.transcript}</ContainedTextBlock>
                </Card>
              )}

              {/* بخش ۶: رونویسی (Transcription) */}
              {(isTranscribePending || isTranscribeProcessing || isTranscribeCompleted || isTranscribeFailed) && (
                <Card
                  size="small"
                  className="custom-font shadow-sm call-center-transcript-card"
                  title={<span className="custom-font font-bold">رونویسی مکالمه (Transcription)</span>}
                  style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
                >
                  {isTranscribePending || isTranscribeProcessing ? (
                    <div className="text-center py-6 custom-font">
                      <Spin tip={<span className="custom-font">در حال رونویسی... (ممکن است چند دقیقه زمان ببرد)</span>} />
                    </div>
                  ) : isTranscribeFailed ? (
                    <Alert
                      type="error"
                      showIcon
                      className="custom-font"
                      message={<span className="custom-font">خطا در رونویسی مکالمه</span>}
                      description={
                        <span className="custom-font">
                          {call.transcribe_error || 'پردازش با خطا مواجه شد. می‌توانید مجدداً تلاش کنید.'}
                        </span>
                      }
                    />
                  ) : (
                    <div className="space-y-4">
                      {call.transcribed_segments?.map((seg, idx) => {
                        const isAgent = seg.speaker_id === 1; // Agent = 1, Client = 2 usually
                        return (
                          <div key={idx} className={`flex flex-col ${isAgent ? 'items-start' : 'items-end'}`}>
                            <div className={`p-3 rounded-xl max-w-2xl ${isAgent ? 'bg-teal-50 dark:bg-teal-900/20 rounded-tr-none' : 'bg-gray-100 dark:bg-gray-800 rounded-tl-none'}`}>
                              <div className={`flex items-center gap-2 mb-1 ${isAgent ? 'justify-start' : 'justify-end'}`}>
                                <span className="font-bold text-xs">{isAgent ? 'اپراتور' : 'مخاطب'}</span>
                                <span className="text-gray-400 text-xs" dir="ltr">{seg.time_range}</span>
                                {seg.emotion && (
                                  <Tag color="blue" className="text-xs m-0">{seg.emotion}</Tag>
                                )}
                              </div>
                              <div className="text-sm">
                                {seg.text}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {(!call.transcribed_segments || call.transcribed_segments.length === 0) && (
                         <Empty description="بخش‌های رونویسی پیدا نشد." />
                      )}
                    </div>
                  )}
                </Card>
              )}

              {!isAiPending && !isAiCompleted && !isAiFailed && (
                <div className="text-center text-gray-500 custom-font text-sm py-2">
                  برای این مکالمه خلاصه‌سازی هوش مصنوعی انجام نشده است. برای تولید خلاصه و رونوشت متنی، روی دکمه «خلاصه‌سازی با AI» در بالای صفحه کلیک کنید.
                </div>
              )}
            </div>
          </Card>
        </div>
      </ConfigProvider>
    </MainLayout>
  );
};

export default ViewCallCenterCall;
